'use client';

import * as React from 'react';

import { ArrowLeft } from 'lucide-react';

import { Text } from '@/hen-ui/atomics/Text';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { carouselNavButtonVariants } from '@/hen-ui/composites/Carousel/carousel.variants';
import { Flex } from '@/hen-ui/composites/Flex';
import { cn } from '@/hen-ui/utils';
import { useReducedMotion } from '@/hen-ui/hooks/useReducedMotion';
import {
  useEnclosingModal,
  useHideModalCloseButton,
  useHideModalTitle,
  type ModalOpenPayload,
} from '@/hen-ui/composites/Modal/ModalHost';

import {
  MediaGallery,
  MediaGalleryLibrary,
  flattenMediaGroups,
  type MediaGroup,
} from './MediaGallery';

export interface MediaGalleryViewerProps {
  /** Missing until photos are bound. The library renders empty instead of throwing. */
  groups?: MediaGroup[];
  imageBorder?: boolean;
  loadingLabel?: string;
  errorLabel?: string;
  counterVariant?: 'b1' | 'b2' | 'b3';
  counterWeight?: 'regular' | 'medium' | 'semibold' | 'bold';
  background?: 'default' | 'chrome';
}

type ViewerPhase = 'library' | 'carousel';

/**
 * Library and one-up carousel for one popup. Opened with a photo position
 * (`modal.open` with `index`), the library opens scrolled to that photo.
 * Opened without one, as See gallery does, it starts at the top. Choosing a
 * photo in the library shows the carousel, and Back returns to the library.
 *
 * The popup around it owns the title and the close button, so give that
 * Modal a title: it is what names the dialog for screen readers.
 */
export const MediaGalleryViewer = ({
  groups,
  imageBorder = false,
  loadingLabel,
  errorLabel,
  counterVariant = 'b3',
  counterWeight = 'bold',
  background = 'default',
}: MediaGalleryViewerProps) => {
  const enclosingModal = useEnclosingModal();
  const payload = enclosingModal?.payload;
  const requestClose = enclosingModal?.requestClose;
  const viewerRef = React.useRef<HTMLElement | null>(null);
  const [viewerHost, setViewerHost] = React.useState<HTMLElement | null>(null);
  const setViewerRef = React.useCallback((node: HTMLElement | null) => {
    viewerRef.current = node;
    setViewerHost(node);
  }, []);
  // The editor can open this before groups is bound. Normalize once and pass
  // the array down, so the library never reads a missing value.
  const safeGroups = React.useMemo(
    () => (Array.isArray(groups) ? groups : []),
    [groups],
  );
  const items = React.useMemo(
    () => flattenMediaGroups(safeGroups),
    [safeGroups],
  );
  const [view, setView] = React.useState(() => viewFromPayload(payload));
  const { phase } = view;
  // Bound photos can arrive after the popup opens, so the requested position is
  // kept as asked and fitted to whatever has loaded.
  const index = clampViewerIndex(view.index, items.length);
  // In a popup the carousel fades in and out like the fullscreen popup does.
  // Bind to the viewer's window so an authoring-preview iframe is consulted.
  const reducedMotion = useReducedMotion(viewerHost);
  const animates = enclosingModal !== undefined && !reducedMotion;
  // Back fades the carousel out over the returning library before removing it.
  const [leaving, setLeaving] = React.useState(false);
  const showsCarousel = phase === 'carousel' && !leaving;
  // Back is the way out of the carousel; the popup close returns with the
  // library. The carousel header shows the position, so the popup title steps
  // aside too.
  useHideModalCloseButton(showsCarousel);
  useHideModalTitle(showsCarousel);
  const setPhase = (next: ViewerPhase) =>
    setView((current) => ({ ...current, phase: next }));
  const setIndex = (next: number) =>
    setView((current) => ({ ...current, index: next }));

  const finishLeaving = React.useCallback(() => {
    setLeaving(false);
    setView((current) => ({ ...current, phase: 'library' }));
  }, []);
  const backToLibrary = React.useCallback(() => {
    if (animates) setLeaving(true);
    else setPhase('library');
  }, [animates]);
  // `animationend` does not fire when the animation is skipped, for example
  // in a background tab, so the fade has a deadline. Use the viewer's window
  // so an authoring-preview iframe is not timed from the parent editor.
  React.useEffect(() => {
    if (!leaving) return undefined;
    const view = viewerRef.current?.ownerDocument.defaultView;
    if (!view) return undefined;
    const timer = view.setTimeout(finishLeaving, 600);
    return () => view.clearTimeout(timer);
  }, [finishLeaving, leaving]);

  // Radix owns the dialog Escape path. Capture it first only while this
  // active viewer is showing the nested carousel, so the first Escape steps
  // back and the next one can close the restored library normally. Register
  // on the viewer's window so key events inside a preview iframe reach it.
  React.useEffect(() => {
    if (!showsCarousel) return undefined;
    const view = viewerRef.current?.ownerDocument.defaultView;
    if (!view) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      const viewer = viewerRef.current;
      if (event.key !== 'Escape' || !viewer || !viewerIsActive(viewer)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      backToLibrary();
    };
    view.addEventListener('keydown', onKeyDown, true);
    return () => view.removeEventListener('keydown', onKeyDown, true);
  }, [backToLibrary, showsCarousel]);

  // The fullscreen dialog covers the overlay, so a miss on the gallery never
  // reaches Radix. A finished primary click on blank dialog surface closes
  // it. A press does not: scrollbar drags, right-clicks, and touch swipes
  // start with pointerdown and must be allowed to finish.
  React.useEffect(() => {
    if (phase !== 'library' || !requestClose) return undefined;
    const viewer = viewerRef.current;
    const dialog = viewer?.closest<HTMLElement>('[data-slot="dialog-content"]');
    if (!viewer || !dialog) return undefined;

    const onClick = (event: MouseEvent) => {
      if (event.button !== 0 || isScrollbarGutter(event)) return;
      if (pointerMissesLibrary(event.target, viewer)) requestClose();
    };
    dialog.addEventListener('click', onClick);
    return () => dialog.removeEventListener('click', onClick);
  }, [phase, requestClose]);

  // Each open hands over a new payload, so reopening while mounted restarts.
  const [appliedPayload, setAppliedPayload] = React.useState(payload);
  if (appliedPayload !== payload) {
    setAppliedPayload(payload);
    setLeaving(false);
    setView(viewFromPayload(payload));
  }

  const library = (
    <MediaGalleryLibrary
      groups={safeGroups}
      positionIndex={index}
      imageBorder={imageBorder}
      loadingLabel={loadingLabel}
      errorLabel={errorLabel}
      onSelectedIndexChange={(next) => {
        setIndex(next);
        setPhase('carousel');
      }}
    />
  );

  if (phase === 'library') {
    return (
      <Flex
        ref={setViewerRef}
        direction='column'
        gap='none'
        data-testid='media-gallery-viewer'
        data-hen-id={COMPONENT_IDS.MediaGalleryViewer}
        data-phase='library'
        className='h-full min-h-0'
      >
        {library}
      </Flex>
    );
  }

  const carouselHeader = (
    <Flex
      direction='row'
      align='center'
      gap='xs'
      data-testid='media-viewer-carousel-header'
      // From md the carousel stage is a grid: Back sits in the previous-arrow
      // column, outside the photo, and the count starts at the photo's edge.
      className='relative z-10 shrink-0 md:contents'
    >
      {/* hen-ui-allow-raw-html -- match beside onDark arrows; chip hover/color diverge */}
      <button
        type='button'
        aria-label='Back to all photos'
        className={cn(
          // Flex + focus ring from shadcn Button; hover/press come from onDark.
          'inline-flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 [&_svg]:pointer-events-none [&_svg]:shrink-0',
          carouselNavButtonVariants({ design: 'onDark', size: 'lg' }),
          'relative md:col-start-1 md:row-start-1 md:justify-self-center',
        )}
        // The focused library tile unmounts with this swap, so Back takes
        // focus instead of leaving it on the page.
        autoFocus
        onClick={backToLibrary}
      >
        <ArrowLeft aria-hidden='true' />
      </button>
      <Text
        variant={counterVariant}
        weight={counterWeight}
        className='text-white md:col-start-2 md:row-start-1 md:justify-self-start'
        aria-live='polite'
      >
        {index + 1} of {items.length}
      </Text>
    </Flex>
  );

  const carousel = (
    <Flex
      direction='column'
      gap='none'
      data-testid='media-viewer-carousel'
      data-leaving={leaving || undefined}
      className={cn(
        'min-h-0',
        background === 'chrome' ? 'bg-surface-chrome' : 'bg-neutral-900',
        // In a popup the dark backdrop covers it edge to edge, over the
        // popup's spacing and anything authored around the viewer, such as
        // tabs.
        enclosingModal ? 'fixed inset-0 z-50' : 'h-full',
        animates &&
          cn(
            '[--motion-duration:var(--motion-duration-slower,450ms)]',
            leaving
              ? 'pointer-events-none animate-fade-out-motion [animation-fill-mode:forwards]'
              : 'animate-fade-in-motion',
          ),
      )}
      onAnimationEnd={(event) => {
        if (leaving && event.target === event.currentTarget) finishLeaving();
      }}
      onClick={(event) => {
        if (event.button !== 0) return;
        if (pointerHitsControlOrPhoto(event.target)) return;
        // onDark unavailable beside arrows usually absorb the click themselves.
        // If pointer-events CSS fails to apply, the press lands on this field —
        // treat coordinates over a dimmed Prev/Next as the arrow, not a miss.
        if (
          pointerOverDisabledCarouselArrow(
            event.currentTarget,
            event.clientX,
            event.clientY,
          )
        ) {
          return;
        }
        backToLibrary();
      }}
    >
      {/* Below md: Back stays at the top of a photo-width column; the photo
          centers in the room under it. From md the column + arrows center. */}
      <Flex
        direction='column'
        gap='none'
        data-testid='media-viewer-carousel-layout'
        className='mx-auto h-full min-h-0 w-full max-w-content md:justify-center'
      >
        <Flex
          gap='none'
          align='stretch'
          justify='start'
          data-testid='media-viewer-carousel-photo'
          className='h-full min-h-0 flex-1 px-sm pt-sm md:h-auto md:flex-none md:justify-center md:overflow-hidden md:px-0 md:pt-0 md:pb-lg'
        >
          <MediaGallery
            items={items}
            label='Photos'
            selectedIndex={index}
            onSelectedIndexChange={setIndex}
            showAsCarousel
            showCount={false}
            imageBorder={false}
            imageFit='contain'
            carouselHeader={carouselHeader}
          />
        </Flex>
      </Flex>
    </Flex>
  );

  // While the carousel fades out, the library is already back underneath it,
  // so the visitor lands on it as the carousel clears.
  return (
    <Flex
      ref={setViewerRef}
      direction='column'
      gap='none'
      data-testid='media-gallery-viewer'
      data-hen-id={COMPONENT_IDS.MediaGalleryViewer}
      data-phase={leaving ? 'library' : 'carousel'}
      className='h-full min-h-0'
    >
      {leaving ? library : null}
      {carousel}
    </Flex>
  );
};

MediaGalleryViewer.displayName = 'MediaGalleryViewer';

const clampViewerIndex = (index: number, length: number): number => {
  if (length === 0) return 0;
  return Math.min(Math.max(index, 0), length - 1);
};

/** Action params can arrive as text, so `"3"` counts as a position too. */
const readPayloadIndex = (
  payload: ModalOpenPayload | undefined,
): number | undefined => {
  const raw = payload?.index;
  const value =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw.trim() !== ''
        ? Number(raw)
        : Number.NaN;
  return Number.isFinite(value) ? Math.floor(value) : undefined;
};

const CONTROL_OR_PHOTO_SELECTOR =
  'button, a[href], input, select, textarea, img, [role="button"], [role="tab"], [role="tablist"]';

/**
 * Preview dialogs render in another frame, so `instanceof Element` is false
 * for their nodes. `nodeType` is the same number in every frame.
 */
function eventElement(target: EventTarget | null): Element | null {
  if (target == null || typeof target !== 'object' || !('nodeType' in target)) {
    return null;
  }
  const node = target as { nodeType: number; parentElement?: Element | null };
  if (node.nodeType === Node.ELEMENT_NODE) return target as Element;
  if (node.nodeType === Node.TEXT_NODE) return node.parentElement ?? null;
  return null;
}

/** Inactive tab panels stay mounted. Their gallery must not dismiss the popup. */
function viewerIsActive(viewer: HTMLElement): boolean {
  return viewer.closest('[hidden], [data-state="inactive"]') == null;
}

/** A press on the scrollbar gutter is a scroll, not a click on blank surface. */
function isScrollbarGutter(event: MouseEvent): boolean {
  const element = eventElement(event.target);
  if (!element || element.getAttribute('data-slot') !== 'dialog-scroll') {
    return false;
  }
  const scroller = element as HTMLElement;
  const { offsetX, offsetY } = event;
  if (typeof offsetX !== 'number' || typeof offsetY !== 'number') return false;
  return (
    offsetX < 0 ||
    offsetY < 0 ||
    offsetX >= scroller.clientWidth ||
    offsetY >= scroller.clientHeight
  );
}

/** True when the pointer lands on blank dialog surface, not the gallery or its chrome. */
function pointerMissesLibrary(
  target: EventTarget | null,
  viewer: HTMLElement,
): boolean {
  const element = eventElement(target);
  if (!element || !viewerIsActive(viewer)) return false;
  const dialog = viewer.closest('[data-slot="dialog-content"]');
  if (!dialog?.contains(element) || viewer.contains(element)) return false;
  if (element.closest('[data-slot="dialog-title-row"]')) return false;
  if (element.closest('[data-slot="dialog-close"]')) return false;
  if (element.closest(CONTROL_OR_PHOTO_SELECTOR)) return false;
  return true;
}

/** Back, arrows, and the photo keep their own actions. The dark field does not. */
function pointerHitsControlOrPhoto(target: EventTarget | null): boolean {
  return eventElement(target)?.closest(CONTROL_OR_PHOTO_SELECTOR) != null;
}

/** True when the press lands on a dimmed Previous/Next that cannot move. */
function pointerOverDisabledCarouselArrow(
  root: Element,
  clientX: number,
  clientY: number,
): boolean {
  for (const arrow of Array.from(
    root.querySelectorAll(
      '[data-slot="carousel-previous"][aria-disabled="true"], [data-slot="carousel-next"][aria-disabled="true"]',
    ),
  )) {
    const { left, right, top, bottom } = arrow.getBoundingClientRect();
    if (
      clientX >= left &&
      clientX < right &&
      clientY >= top &&
      clientY < bottom
    ) {
      return true;
    }
  }
  return false;
}

// Every open starts in the library; only choosing a photo there shows the carousel.
const viewFromPayload = (
  payload: ModalOpenPayload | undefined,
): { phase: ViewerPhase; index: number } => ({
  phase: 'library',
  index: readPayloadIndex(payload) ?? 0,
});
